// Family plan helpers — loan-amount driven (mirrors server/simulator/helpers/family_plans.py)

export const FAMILY_MIN_LOAN = 10000
export const FAMILY_MAX_LOAN = 440000
export const FAMILY_STEP = 10000
export const UNIT_50 = 50000
export const UNIT_10 = 10000
export const GRANT_PER_10K = 600

// "יותר מ-3 ילדים" - no family tier covers it, so it blocks right away (before the age question)
export const FAMILY_MORE_CHILDREN = 'more'

// How many children the donor already has (family mode, "יש לי כבר ילדים")
export const familyChildrenOptions = [
    { value: '1', label: 'ילד אחד' },
    { value: '2', label: '2 ילדים' },
    { value: '3', label: '3 ילדים' },
    { value: FAMILY_MORE_CHILDREN, label: 'יותר מ-3 ילדים' },
]

// Tier rank - the tighter the tier (lower max age, then fewer children), the cheaper it is.
const tierRank = (plan) => (Number(plan?.maxAge) || 0) * 100 + (Number(plan?.children) || 0)

// The tier that fits a family of `children` children whose oldest is `age` years old:
// a tier covers the family when children <= tier.children && age <= tier.maxAge; of all
// covering tiers only the tightest one is offered (both its variants - with/without inflation).
// `children` may be 0 (the "no children yet" sub-mode, which never asks the question).
export function familyTierPlans(familyPlans, children, age) {
    const count = Number(children)
    if (!familyPlans?.length || children === FAMILY_MORE_CHILDREN || children === null || children === undefined
        || !Number.isFinite(count) || age === null || age === undefined) return []
    const covering = familyPlans.filter(plan => count <= (Number(plan?.children) || 0) && age <= (Number(plan?.maxAge) || 0))
    if (!covering.length) return []
    const tier = covering.reduce((best, plan) => (tierRank(plan) < tierRank(best)) ? plan : best)
    return covering.filter(plan => plan.children === tier.children && plan.maxAge === tier.maxAge)
}

// True when the family has a matching tier (i.e. is eligible for the family plan).
export function familyTierAvailable(familyPlans, children, age) {
    return familyTierPlans(familyPlans, children, age).length > 0
}

// Split a loan into max 50,000-value units + remaining 10,000-value units.
// e.g. 130000 -> { num50: 2, num10: 3 }
export function splitFamilyLoan(loan) {
    const amount = Number(loan) || 0
    if (amount <= 0) return { num50: 0, num10: 0 }
    const num50 = Math.floor(amount / UNIT_50)
    const num10 = Math.floor((amount - num50 * UNIT_50) / UNIT_10)
    return { num50, num10 }
}

// Monthly donation summed from the 50k/10k unit split.
export function familyMonthlyDonation(familyPlan, loan) {
    if (!familyPlan?.unitPlans) return 0
    const { num50, num10 } = splitFamilyLoan(loan)
    const pay50 = Number(familyPlan.unitPlans[UNIT_50]?.monthlyPay) || 0
    const pay10 = Number(familyPlan.unitPlans[UNIT_10]?.monthlyPay) || 0
    return num50 * pay50 + num10 * pay10
}

// Grant per child — 600 for each 10,000 of loan.
export function familyGrant(loan) {
    const amount = Number(loan) || 0
    return (amount / UNIT_10) * GRANT_PER_10K
}

// True when a kid's plan is a grouped family plan (premium / premium-with-inflation).
export function isFamilyPlan(plan) {
    return !!plan?.unitPlans
}

// Per-kid display totals for the summary tables — works for both standard and family kids.
export function kidDisplayTotals(kid) {
    const plan = kid?.plan || {}
    if (isFamilyPlan(plan)) {
        const s = familyKidSummary(plan, kid?.loanAmount)
        const p50 = plan.unitPlans?.[UNIT_50]
        return {
            units: s.units,
            monthlyDonation: s.monthlyDonation,
            payments: plan.monthsSave,
            totalDonation: s.totalDonation,
            monthlyReturn: s.monthlyReturn,
            returnPayments: (p50?.monthlyReturn) ? Math.ceil(p50.loan / p50.monthlyReturn) : 0,
            totalLoan: s.loan,
            grant: s.grant,
        }
    }
    const units = Number(kid?.units) || 0
    return {
        units,
        monthlyDonation: plan.monthlyPay * units,
        payments: plan.totalPay / plan.monthlyPay,
        totalDonation: plan.totalPay * units,
        monthlyReturn: plan.monthlyReturn * units,
        returnPayments: Math.ceil(plan.loan / plan.monthlyReturn),
        totalLoan: plan.loan * units,
        grant: plan.return * units,
    }
}

// Full per-kid totals for a family loan, summed across the 50k/10k split.
export function familyKidSummary(plan, loan) {
    const { num50, num10 } = splitFamilyLoan(loan)
    const p50 = plan?.unitPlans?.[UNIT_50]
    const p10 = plan?.unitPlans?.[UNIT_10]
    const pick = (p, key) => Number(p?.[key]) || 0
    return {
        units: num50 + num10,
        monthlyDonation: num50 * pick(p50, 'monthlyPay') + num10 * pick(p10, 'monthlyPay'),
        totalDonation: num50 * pick(p50, 'totalPay') + num10 * pick(p10, 'totalPay'),
        monthlyReturn: num50 * pick(p50, 'monthlyReturn') + num10 * pick(p10, 'monthlyReturn'),
        loan: Number(loan) || 0,
        grant: familyGrant(loan),
    }
}
